/**
 * Unify Appointment FHIR model.
 *
 * Represents a scheduled event on a provider's calendar. Appointments fall
 * into two categories:
 *
 * - **Billable** — at least one `SBJ` patient participant + session type.
 *   Creates an Encounter and links to an EpisodeOfCare. A ChargeItem is not written by this flow.
 * - **Non-billable** — provider meetings (no `SBJ`) or intake appointments
 *   (screening-call / consultation-call). No Encounter.
 *
 * ┌──────────────────────────┬─────────────────────────────────────────────┐
 * │ FHIR field               │ Unify usage                                │
 * ├──────────────────────────┼─────────────────────────────────────────────┤
 * │ status                   │ proposed | booked | fulfilled | cancelled  │
 * │                          │ | noshow                                   │
 * │ start / end              │ ISO 8601 datetime with timezone            │
 * │ minutesDuration          │ Scheduled duration in minutes              │
 * │ serviceCategory          │ Highest-level grouping (CodeableConcept[]) │
 * │ serviceType              │ Mid-level service type (CodeableConcept[]) │
 * │ appointmentType          │ Modality: in-person | telehealth | hybrid  │
 * │ description              │ Free-text summary                          │
 * │ cancelationReason        │ CodeableConcept (cancellation-reason CS)   │
 * │ created                  │ When first booked                          │
 * │ slot                     │ Reference<Slot>[] — one per actor Schedule │
 * │ participant              │ PPRF (provider) + SBJ (patient) + others   │
 * ├──────────────────────────┼─────────────────────────────────────────────┤
 * │ EXTENSIONS               │                                            │
 * │ video-meeting-url        │ valueUrl — video call link (Unify /join/)  │
 * │ chime-session/*          │ flat — Chime session state (staff-only)    │
 * │ recurring-pattern        │ valueCode — daily|weekly|biweekly|monthly  │
 * │ episode-of-care-context  │ valueReference → EpisodeOfCare             │
 * │ attendance-status        │ valueCode on participant — per-person      │
 * │ attendance-source        │ valueCoding — chime-auto | manual          │
 * │ participant-link-code    │ valueString on client participant — `?p=`  │
 * │ session-transcript       │ nested — the appointment's S3 transcript   │
 * │ reservation-session      │ valueString — intake session ID            │
 * │ reservation-expires-at   │ valueDateTime — intake reservation expiry  │
 * └──────────────────────────┴─────────────────────────────────────────────┘
 *
 * Participant type codes (system: v3-ParticipationType):
 *   PPRF  — primary performer (Practitioner, exactly 1)
 *   SPRF  — secondary performer (Practitioner)
 *   SBJ   — billed client (Patient)
 *   PART  — supporting participant (RelatedPerson, never Patient)
 *   LOC   — practice office (Location)
 *   DEV   — portable equipment (Device)
 *
 * Participant type codes (system: unify participant-type):
 *   ROOM  — specific room (Location)
 *
 * Participant-level status: accepted | declined | tentative | needs-action
 *
 * Related tagging: linked QuestionnaireResponses carry a meta.tag with
 * system = APPOINTMENT_TAG_SYSTEM and code = Appointment.id.
 */
import type {
  Appointment,
  AppointmentParticipant,
  Coding,
  Extension,
  Identifier,
  Reference,
} from '@medplum/fhirtypes';

import {
  ATTENDANCE_SOURCE_EXT,
  ATTENDANCE_STATUS_EXT,
  CHIME_MEETING_IDENTIFIER_SYSTEM,
  CHIME_SESSION_EXTS,
  CANCELLATION_REASON_SYSTEM,
  EPISODE_OF_CARE_CONTEXT_EXT,
  GUEST_LINK_CODE_IDENTIFIER_SYSTEM,
  PARTICIPANT_LINK_CODE_EXT,
  RECURRING_PATTERN_EXT,
  RESERVATION_EXPIRES_AT_EXT,
  RESERVATION_SESSION_EXT,
  SESSION_NOTE_PIPELINE_STATUS_CODES,
  SESSION_NOTE_PIPELINE_STATUS_EXT,
  SESSION_TRANSCRIPT_EXT,
  SESSION_TRANSCRIPT_PIPELINE_IDENTIFIER_SYSTEM,
  SESSION_TRANSCRIPT_PIPELINE_QUEUED_VALUE,
  SESSION_TRANSCRIPT_SUB,
  TELEHEALTH_GUEST_LINK_EXT,
  VIDEO_MEETING_URL_EXT,
  type SessionNotePipelineStatus,
} from '../constants';

/**
 * Top-level Appointment extensions with `url`. Client and proxy policies hide the whole `Appointment.extension`
 * (design Decision 35), so session state and other staff-only data are plain extensions.
 */
export function getAppointmentExtensions(resource: Appointment, url: string): Extension[] {
  return (resource.extension ?? []).filter((e) => e.url === url);
}

/** Replace every top-level extension with `url` by `next` (empty removes them). Assigns a new array. */
export function setAppointmentExtensions(resource: Appointment, url: string, next: Extension[]): void {
  const extension = [...(resource.extension ?? []).filter((e) => e.url !== url), ...next];
  resource.extension = extension.length ? extension : undefined;
}

// ═══════════════════════════════════════════════════════════════════════
//  Literal unions
// ═══════════════════════════════════════════════════════════════════════

/** Appointment-level status values used by Unify. */
export type AppointmentStatus =
  | 'proposed'
  | 'booked'
  | 'fulfilled'
  | 'cancelled'
  | 'noshow';

/** Recurring pattern codes. */
export type RecurringPattern =
  | 'daily'
  | 'weekly'
  | 'biweekly'
  | 'monthly';

/** Cancellation reason codes (CodeSystem: cancellation-reason). */
export type CancellationReasonCode =
  | 'clinician-cancelled'
  | 'client-cancelled'
  | 'client-late-cancelled'
  | 'reservation-expired';

/** How attendance was determined (on the attendance-source extension). */
export type AttendanceSource = 'chime-auto' | 'manual';

export type ChimeSessionStatus = 'created' | 'active' | 'ended';
/** A client or guest still waiting this long after `start` is told the provider hasn't joined. */
export const CHIME_PROVIDER_LATE_MS = 15 * 60 * 1000;

/**
 * True when `start` is more than 15 minutes past and no provider has started the session (no
 * active meeting): the waiting screens add "Your provider hasn't joined yet."
 */
export function chimeProviderLate(
  session: Pick<ChimeSession, 'status' | 'meetingId'> | undefined,
  startMs: number,
  nowMs: number,
): boolean {
  if (Number.isNaN(startMs) || nowMs <= startMs + CHIME_PROVIDER_LATE_MS) return false;
  return !(session?.status === 'active' && session.meetingId);
}

/** The join window closes this long after `Appointment.end`. */
const JOIN_WINDOW_CLOSES_MS = 60 * 60 * 1000;
/** The join window closes this long after `Appointment.start` when there is no `end`. */
const JOIN_WINDOW_CLOSES_NO_END_MS = 2 * 60 * 60 * 1000;

/** The join window closes `end` + 60 min, or `start` + 2 h when there is no `end`. */
export function joinWindowClosesMs(appt: Pick<Appointment, 'end'>, startMs: number): number {
  const endMs = appt.end ? Date.parse(appt.end) : Number.NaN;
  return Number.isNaN(endMs) ? startMs + JOIN_WINDOW_CLOSES_NO_END_MS : endMs + JOIN_WINDOW_CLOSES_MS;
}

/**
 * True for a `noshow` or `fulfilled` outcome that `chime-events` derived (`attendance-source =
 * chime-auto`) while the join window is still open: the client may yet arrive (or return), so live
 * actions treat it as active and the shareable link is kept. A manual outcome is never joinable, and
 * neither is any outcome once the window closes.
 */
export function chimeAutoOutcomeJoinable(appt: Appointment, nowMs: number): boolean {
  if ((appt.status !== 'noshow' && appt.status !== 'fulfilled') || getAttendanceSource(appt)?.code !== 'chime-auto') return false;
  return nowMs <= joinWindowClosesMs(appt, appt.start ? Date.parse(appt.start) : Number.NaN);
}

/**
 * Chime session state (design Decisions 34 and 39): the meeting id identifier plus three flat extensions. Whether the
 * session is recording comes from Chime (the recorder in the roster, or the pipeline's own status); `mediaPipelineId`
 * is the current or last capture: the handle a stop deletes (Chime offers no per-meeting pipeline lookup) and the one
 * `chime-events` matches pipeline events and transcript results to.
 */
export interface ChimeSession {
  meetingId?: string;
  meetingArn?: string;
  status?: ChimeSessionStatus;
  mediaPipelineId?: string;
}

/** Participant-level status values (FHIR R4). */
export type ParticipantStatus =
  | 'accepted'
  | 'declined'
  | 'tentative'
  | 'needs-action';

// ═══════════════════════════════════════════════════════════════════════
//  Participant helpers
// ═══════════════════════════════════════════════════════════════════════

/** Find the first participant whose actor reference starts with the given resource type. */
function findParticipantByActorType(
  resource: Appointment,
  prefix: string,
): AppointmentParticipant | undefined {
  return resource.participant?.find((p) => p.actor?.reference?.startsWith(prefix));
}

/** All participants whose actor reference starts with the given resource type. */
function filterParticipantsByActorType(
  resource: Appointment,
  prefix: string,
): AppointmentParticipant[] {
  return resource.participant?.filter((p) => p.actor?.reference?.startsWith(prefix)) ?? [];
}

/** First Patient participant (SBJ — the billed client). */
export function getPatientParticipant(resource: Appointment): AppointmentParticipant | undefined {
  return findParticipantByActorType(resource, 'Patient/');
}

/** All Patient participants (relevant for group therapy with multiple SBJ). */
export function getPatientParticipants(resource: Appointment): AppointmentParticipant[] {
  return filterParticipantsByActorType(resource, 'Patient/');
}

/** First Practitioner participant (typically PPRF — primary performer). */
export function getPractitionerParticipant(resource: Appointment): AppointmentParticipant | undefined {
  return findParticipantByActorType(resource, 'Practitioner/');
}

/** All Practitioner participants (PPRF + SPRFs). */
export function getPractitionerParticipants(resource: Appointment): AppointmentParticipant[] {
  return filterParticipantsByActorType(resource, 'Practitioner/');
}

/** All RelatedPerson participants (PART — supporting family/couples members). */
export function getRelatedPersonParticipants(resource: Appointment): AppointmentParticipant[] {
  return filterParticipantsByActorType(resource, 'RelatedPerson/');
}

/**
 * True when an edit leaves the Appointment's `start` instant equal (format-insensitive). A missing or
 * unparseable value is false, the safe direction. An end-only change resizes the same session; a start
 * move is a reschedule.
 */
export function appointmentStartUnchanged(
  existing: Pick<Appointment, 'start'>,
  start: string | undefined,
): boolean {
  return existing.start !== undefined && start !== undefined && Date.parse(existing.start) === Date.parse(start);
}

/**
 * Merge participants rebuilt by a scheduling form with the ones already stored.
 *
 * Scheduling forms rebuild `Appointment.participant` from their own fields, which
 * silently discards data they do not render. This keeps two things:
 *
 * 1. Per-participant extensions (attendance status, location) are carried forward onto
 *    matching actors, since scheduling never owns attendance. `period` (presence) is
 *    carried too, but only with `keepPeriods` (start unchanged).
 * 2. With `keepUnmatchedPatients`, `Patient` participants the form does not render
 *    are retained — the cohort roster of a group session, whose membership comes
 *    from the Group, not this form. Practitioners (primary facilitator plus
 *    co-facilitators), locations, and devices are all form-owned, so unmatched ones
 *    are dropped: a room change must not leave the old room booked, and removing a
 *    co-facilitator must actually remove them.
 */
export function mergeAppointmentParticipants(
  existing: AppointmentParticipant[] | undefined,
  formParticipants: AppointmentParticipant[],
  options: { keepUnmatchedPatients: boolean; keepPeriods: boolean },
): AppointmentParticipant[] {
  const existingByRef = new Map<string, AppointmentParticipant>();
  for (const participant of existing ?? []) {
    const reference = participant.actor?.reference;
    if (reference) existingByRef.set(reference, participant);
  }

  const merged = formParticipants.map((participant) => {
    const reference = participant.actor?.reference;
    const prior = reference ? existingByRef.get(reference) : undefined;
    if (!prior) return participant;
    // `period` (Chime presence) is not rendered by forms either; a period the form supplies wins. It
    // is carried only when the start did not change (`keepPeriods`): presence belongs to the old session.
    const period = participant.period ?? (options.keepPeriods ? prior.period : undefined);
    // A client's RSVP pairs with its carried attendance (declined = absent), so the form's
    // hard-coded `accepted` must not overwrite it.
    const status = (isClientAppointmentParticipant(prior) && prior.status) || participant.status;
    if (!prior.extension?.length && !period && status === participant.status) return participant;
    return {
      ...participant,
      status,
      ...(prior.extension?.length ? { extension: prior.extension } : {}),
      ...(period ? { period } : {}),
    };
  });

  if (!options.keepUnmatchedPatients) return merged;

  const writtenRefs = new Set(
    formParticipants
      .map((p) => p.actor?.reference)
      .filter((reference): reference is string => Boolean(reference)),
  );
  const carriedOverPatients = (existing ?? []).filter((participant) => {
    const reference = participant.actor?.reference;
    return Boolean(reference) && !writtenRefs.has(reference as string)
      && (reference as string).startsWith('Patient/');
  });
  // A roster patient carries its extensions (attendance) as before, but its `period` only when the
  // start did not change: presence belongs to the old session.
  const rosterPatients = options.keepPeriods
    ? carriedOverPatients
    : carriedOverPatients.map((participant) => ({ ...participant, period: undefined }));
  return [...merged, ...rosterPatients];
}

/** Attendance status extension on a participant entry (e.g. "attended", "no-show"). */
export function getParticipantAttendanceStatus(
  participant: AppointmentParticipant,
): string | undefined {
  return participant.extension?.find((e) => e.url === ATTENDANCE_STATUS_EXT)?.valueCode;
}

/** Set the attendance-status extension on a participant entry. */
export function setParticipantAttendanceStatus(
  participant: AppointmentParticipant,
  status: string | undefined,
): void {
  const others = (participant.extension ?? []).filter((e) => e.url !== ATTENDANCE_STATUS_EXT);
  participant.extension = status
    ? [...others, { url: ATTENDANCE_STATUS_EXT, valueCode: status }]
    : others.length ? others : undefined;
}

/** True for billed clients (Patient) and supporting session members (RelatedPerson). */
export function isClientAppointmentParticipant(participant: AppointmentParticipant): boolean {
  const ref = participant.actor?.reference ?? '';
  return ref.startsWith('Patient/') || ref.startsWith('RelatedPerson/');
}

/** Personal-link code on a participant entry (only client participants carry one). */
export function getParticipantLinkCode(participant: AppointmentParticipant): string | undefined {
  return participant.extension?.find((e) => e.url === PARTICIPANT_LINK_CODE_EXT)?.valueString;
}

/** Set or remove the participant-link-code extension on a participant entry. */
export function setParticipantLinkCode(participant: AppointmentParticipant, code: string | undefined): void {
  const others = (participant.extension ?? []).filter((e) => e.url !== PARTICIPANT_LINK_CODE_EXT);
  participant.extension = code
    ? [...others, { url: PARTICIPANT_LINK_CODE_EXT, valueString: code }]
    : others.length ? others : undefined;
}

/** The client participant (Patient or RelatedPerson) whose personal-link code equals `code`. */
export function findParticipantByLinkCode(
  resource: Appointment,
  code: string | undefined,
): AppointmentParticipant | undefined {
  if (!code) return undefined;
  return resource.participant?.find(
    (p) => isClientAppointmentParticipant(p) && getParticipantLinkCode(p) === code,
  );
}

const ATTENDANCE_ALIAS: Record<string, string> = {
  attended: 'attended',
  completed: 'attended',
  'client-no-show': 'client-no-show',
  'no-show': 'client-no-show',
  noshow: 'client-no-show',
  'client-cancelled': 'client-cancelled',
  'client-late-cancelled': 'client-late-cancelled',
  'clinician-cancelled': 'clinician-cancelled',
};

/** Codes a client attendance write may store. Aliases normalize onto this set before the write. */
export const PARTICIPANT_ATTENDANCE_CODES = [
  'proposed',
  'scheduled',
  'attended',
  'client-no-show',
  'client-cancelled',
  'client-late-cancelled',
  'clinician-cancelled',
] as const;

const PARTICIPANT_ATTENDANCE_CODE_SET = new Set<string>(PARTICIPANT_ATTENDANCE_CODES);

const PRESENT_ATTENDANCE = 'attended';
const ABSENT_ATTENDANCE = new Set([
  'client-no-show',
  'client-cancelled',
  'client-late-cancelled',
  'clinician-cancelled',
]);
const PROTECTED_FROM_CLINICIAN_CANCEL = new Set(['client-cancelled', 'client-late-cancelled']);

/** Normalize UI and legacy attendance codes onto the calendar client set. */
export function normalizeParticipantAttendanceCode(raw: string | undefined): string | undefined {
  if (!raw?.trim()) return undefined;
  const key = raw.trim().toLowerCase();
  return ATTENDANCE_ALIAS[key] ?? ATTENDANCE_ALIAS[raw.trim()] ?? raw.trim();
}

/** The stored attendance code, or undefined when the value is not in the calendar set. */
export function canonicalParticipantAttendanceCode(raw: string | undefined): string | undefined {
  const normalized = normalizeParticipantAttendanceCode(raw);
  if (!normalized || !PARTICIPANT_ATTENDANCE_CODE_SET.has(normalized)) return undefined;
  return normalized;
}

/**
 * RSVP to store with an attendance code. An explicit `scheduled` or `proposed` (Requested) is a staff reset (e.g.
 * undoing a mistaken no-show), so it restores `accepted`; other unresolved or absent values leave RSVP unchanged.
 */
export function participantRsvpForAttendance(
  code: string | undefined,
): 'accepted' | 'declined' | undefined {
  const normalized = normalizeParticipantAttendanceCode(code);
  if (normalized === PRESENT_ATTENDANCE || normalized === 'scheduled' || normalized === 'proposed') return 'accepted';
  if (normalized != null && ABSENT_ATTENDANCE.has(normalized)) return 'declined';
  return undefined;
}

export function isResolvedClientAttendance(code: string | undefined): boolean {
  const n = normalizeParticipantAttendanceCode(code);
  return n === PRESENT_ATTENDANCE || (n != null && ABSENT_ATTENDANCE.has(n));
}

export function isProtectedFromClinicianCancel(code: string | undefined): boolean {
  const n = normalizeParticipantAttendanceCode(code);
  return n != null && PROTECTED_FROM_CLINICIAN_CANCEL.has(n);
}

/**
 * Mark client participants declined with clinician-cancelled. Only participants whose attendance
 * is still unrecorded are declined: anyone with a resolved attendance (attended, no-show,
 * client-cancelled, client-late-cancelled, clinician-cancelled) is left untouched.
 */
export function declineClientParticipants(
  participants: AppointmentParticipant[] | undefined,
  attendanceStatus: 'clinician-cancelled',
): AppointmentParticipant[] {
  return (participants ?? []).map((participant) => {
    if (!isClientAppointmentParticipant(participant)) return participant;
    if (isResolvedClientAttendance(getParticipantAttendanceStatus(participant))) {
      return participant;
    }
    const next: AppointmentParticipant = { ...participant, status: 'declined' };
    setParticipantAttendanceStatus(next, attendanceStatus);
    return next;
  });
}

/**
 * Clinician cancel of the remaining roster: declines only clients whose attendance is unrecorded
 * (see `declineClientParticipants`), so recorded attendance is never overwritten.
 * Does not set Appointment.status when attendance derivation yields an outcome, either on the
 * original appointment or after the decline (callers apply `applyAttendanceDerivedStatus`).
 * The one exception: a `proposed` or `booked` appointment whose derivation yields nothing both
 * before and after the decline — a hold, a roster with no Patient participants, or a
 * meeting / time-entry category — resolves to `cancelled` with `clinician-cancelled`.
 * Any other status (`fulfilled`, `noshow`, `cancelled`, `entered-in-error`) is never forced.
 */
export function applyClinicianCancelToAppointment(appointment: Appointment): Appointment {
  const declined: Appointment = {
    ...appointment,
    participant: declineClientParticipants(appointment.participant, 'clinician-cancelled'),
  };
  if (appointment.status !== 'proposed' && appointment.status !== 'booked') return declined;
  if (deriveAppointmentStatusFromAttendance(appointment)) return declined;
  if (deriveAppointmentStatusFromAttendance(declined)) return declined;
  return applyDerivedAppointmentOutcome(declined, {
    status: 'cancelled',
    cancelationReason: 'clinician-cancelled',
  });
}

export type DerivedAppointmentOutcome = {
  status: 'fulfilled' | 'noshow' | 'cancelled';
  cancelationReason?: CancellationReasonCode;
};

const CANCEL_REASON_DISPLAY: Record<CancellationReasonCode, string> = {
  'clinician-cancelled': 'Clinician Cancelled',
  'client-cancelled': 'Client Cancelled',
  'client-late-cancelled': 'Client Late Cancelled',
  'reservation-expired': 'Reservation Expired',
};

function appointmentCategoryCode(appointment: Appointment): string | undefined {
  return appointment.serviceCategory?.[0]?.coding?.[0]?.code;
}

/** Patient SBJ participants drive Appointment.status; RelatedPerson PART does not. */
export function getStatusDrivingParticipants(appointment: Appointment): AppointmentParticipant[] {
  return getPatientParticipants(appointment);
}

/**
 * Derive Appointment.status from Patient attendance.
 * Returns undefined when attendance is incomplete and nobody attended, or the appointment is out of scope
 * (meeting / time-entry, `proposed`, or `entered-in-error`).
 * Any attended client fulfills the appointment even if other clients are still blank.
 */
export function deriveAppointmentStatusFromAttendance(
  appointment: Appointment,
): DerivedAppointmentOutcome | undefined {
  const category = appointmentCategoryCode(appointment);
  if (category === 'meeting' || category === 'time-entry') return undefined;
  if (appointment.status === 'proposed' || appointment.status === 'entered-in-error') return undefined;

  const clients = getStatusDrivingParticipants(appointment);
  if (clients.length === 0) return undefined;

  const codes = clients.map((p) => normalizeParticipantAttendanceCode(getParticipantAttendanceStatus(p)));
  if (codes.some((c) => c === PRESENT_ATTENDANCE)) {
    return { status: 'fulfilled' };
  }
  if (codes.some((c) => !isResolvedClientAttendance(c))) return undefined;
  if (codes.every((c) => c === 'client-no-show')) {
    return { status: 'noshow' };
  }
  if (codes.some((c) => c === 'client-late-cancelled')) {
    return { status: 'cancelled', cancelationReason: 'client-late-cancelled' };
  }
  if (codes.some((c) => c === 'client-cancelled')) {
    return { status: 'cancelled', cancelationReason: 'client-cancelled' };
  }
  return { status: 'cancelled', cancelationReason: 'clinician-cancelled' };
}

export function appointmentMatchesDerivedOutcome(
  appointment: Appointment,
  outcome: DerivedAppointmentOutcome,
): boolean {
  if (appointment.status !== outcome.status) return false;
  if (outcome.status !== 'cancelled') return !appointment.cancelationReason;
  const code = appointment.cancelationReason?.coding?.find((c) => c.system === CANCELLATION_REASON_SYSTEM)?.code;
  return code === outcome.cancelationReason;
}

const APPOINTMENT_NOTE_LOCK_STATUSES = new Set(['signed', 'pending-cosign', 'cosigned']);

/** Provider signature (including pending cosign) freezes attendance and Appointment.status. */
export function appointmentNoteStatusLocksFulfillment(code: string | undefined): boolean {
  return code != null && APPOINTMENT_NOTE_LOCK_STATUSES.has(code);
}

/** Apply a derived outcome when one exists and the appointment does not already match it. */
export function applyAttendanceDerivedStatus(appointment: Appointment): Appointment {
  const outcome = deriveAppointmentStatusFromAttendance(appointment);
  if (!outcome || appointmentMatchesDerivedOutcome(appointment, outcome)) return appointment;
  return applyDerivedAppointmentOutcome(appointment, outcome);
}

export function applyDerivedAppointmentOutcome(
  appointment: Appointment,
  outcome: DerivedAppointmentOutcome,
): Appointment {
  const next: Appointment = { ...appointment, status: outcome.status };
  if (outcome.cancelationReason) {
    next.cancelationReason = {
      coding: [
        {
          system: CANCELLATION_REASON_SYSTEM,
          code: outcome.cancelationReason,
          display: CANCEL_REASON_DISPLAY[outcome.cancelationReason],
        },
      ],
    };
  } else {
    next.cancelationReason = undefined;
  }
  return next;
}

/** Per-client Encounter cascade from that patient's attendance (group). */
export function deriveEncounterOutcomeFromAttendance(
  code: string | undefined,
): { status: 'finished' | 'cancelled'; reasonCancelled?: EncounterCancelReason } | undefined {
  const n = normalizeParticipantAttendanceCode(code);
  if (n === PRESENT_ATTENDANCE) return { status: 'finished' };
  if (n === 'client-no-show') return { status: 'cancelled', reasonCancelled: 'noshow' };
  if (n === 'client-cancelled') return { status: 'cancelled', reasonCancelled: 'client-cancelled' };
  if (n === 'client-late-cancelled') return { status: 'cancelled', reasonCancelled: 'client-late-cancelled' };
  if (n === 'clinician-cancelled') return { status: 'cancelled', reasonCancelled: 'clinician-cancelled' };
  return undefined;
}

export type EncounterCancelReason =
  | 'noshow'
  | 'client-cancelled'
  | 'client-late-cancelled'
  | 'clinician-cancelled';

export function deriveEncounterOutcomeFromAppointment(
  outcome: DerivedAppointmentOutcome,
): { status: 'finished' | 'cancelled'; reasonCancelled?: EncounterCancelReason } {
  if (outcome.status === 'fulfilled') return { status: 'finished' };
  if (outcome.status === 'noshow') return { status: 'cancelled', reasonCancelled: 'noshow' };
  return {
    status: 'cancelled',
    reasonCancelled: (outcome.cancelationReason as EncounterCancelReason | undefined) ?? 'clinician-cancelled',
  };
}

// ═══════════════════════════════════════════════════════════════════════
//  Appointment-level extension getters
// ═══════════════════════════════════════════════════════════════════════

/** Video call link for telehealth appointments (the Unify /join/ link). */
export function getVideoMeetingUrl(resource: Appointment): string | undefined {
  return resource.extension?.find((e) => e.url === VIDEO_MEETING_URL_EXT)?.valueUrl;
}

/** Signed link that lets someone with no Unify account reach the waiting room. */
export function getTelehealthGuestLink(resource: Appointment): string | undefined {
  return resource.extension?.find((e) => e.url === TELEHEALTH_GUEST_LINK_EXT)?.valueUrl;
}

/** Recurring pattern code (daily, weekly, biweekly, monthly). */
export function getRecurringPattern(resource: Appointment): RecurringPattern | undefined {
  return resource.extension?.find((e) => e.url === RECURRING_PATTERN_EXT)
    ?.valueCode as RecurringPattern | undefined;
}

/** EpisodeOfCare context reference (links appointment to an episode). */
export function getApptEocContextRef(resource: Appointment): Reference | undefined {
  // `supportingInformation` first: clients can read it, while client policies hide `extension` (design Decision 35).
  return (
    resource.supportingInformation?.find((r) => r.reference?.startsWith('EpisodeOfCare/')) ??
    resource.extension?.find((e) => e.url === EPISODE_OF_CARE_CONTEXT_EXT)?.valueReference
  );
}

/** Attendance source coding (chime-auto or manual). */
export function getAttendanceSource(resource: Appointment): Coding | undefined {
  return resource.extension?.find((e) => e.url === ATTENDANCE_SOURCE_EXT)?.valueCoding;
}

/** Intake reservation session ID (present only on `proposed` intake appointments). */
export function getReservationSession(resource: Appointment): string | undefined {
  return resource.extension?.find((e) => e.url === RESERVATION_SESSION_EXT)?.valueString;
}

/** Intake reservation expiry timestamp. */
export function getReservationExpiresAt(resource: Appointment): string | undefined {
  return resource.extension?.find((e) => e.url === RESERVATION_EXPIRES_AT_EXT)?.valueDateTime;
}

/** Grace period after scheduled `end` before documentation / missing-note tasks apply. */
export const APPOINTMENT_POST_SESSION_DELAY_MS = 5 * 60 * 1000;

type AppointmentWindowFields = Pick<Appointment, 'start' | 'end' | 'minutesDuration'>;

/** Scheduled session end instant (ms): `end`, else `start` + `minutesDuration`. */
export function getAppointmentEndMs(resource: AppointmentWindowFields): number | undefined {
  if (resource.end) {
    const end = new Date(resource.end).getTime();
    if (!Number.isNaN(end)) return end;
  }
  if (resource.start && resource.minutesDuration != null && resource.minutesDuration > 0) {
    const start = new Date(resource.start).getTime();
    if (!Number.isNaN(start)) return start + resource.minutesDuration * 60_000;
  }
  return undefined;
}

/** True when now is past scheduled end (+ short delay). */
export function isFhirAppointmentPostSession(
  resource: AppointmentWindowFields,
  nowMs = Date.now(),
): boolean {
  const endMs = getAppointmentEndMs(resource);
  if (endMs == null) return false;
  return nowMs > endMs + APPOINTMENT_POST_SESSION_DELAY_MS;
}

// ═══════════════════════════════════════════════════════════════════════
//  Appointment-level extension setters (mutate in place)
// ═══════════════════════════════════════════════════════════════════════

/** Set or remove the video meeting URL extension. */
export function setVideoMeetingUrl(resource: Appointment, url: string | undefined): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== VIDEO_MEETING_URL_EXT);
  resource.extension = url
    ? [...others, { url: VIDEO_MEETING_URL_EXT, valueUrl: url }]
    : others.length ? others : undefined;
}

/** Set or remove the telehealth guest link extension. */
export function setTelehealthGuestLink(resource: Appointment, url: string | undefined): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== TELEHEALTH_GUEST_LINK_EXT);
  resource.extension = url
    ? [...others, { url: TELEHEALTH_GUEST_LINK_EXT, valueUrl: url }]
    : others.length ? others : undefined;
}

/** Set or remove the recurring pattern extension. */
export function setRecurringPattern(
  resource: Appointment,
  pattern: RecurringPattern | undefined,
): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== RECURRING_PATTERN_EXT);
  resource.extension = pattern
    ? [...others, { url: RECURRING_PATTERN_EXT, valueCode: pattern }]
    : others.length ? others : undefined;
}

/**
 * Set or remove the episode-of-care-context extension.
 *
 * Also mirrors the episode reference into `supportingInformation[]` so the
 * standard `Appointment?supporting-info=` search parameter can scope
 * appointments by episode (Medplum has no custom SearchParameter support;
 * episode-scoped AccessPolicies rely on this denormalization).
 */
export function setApptEocContextRef(
  resource: Appointment,
  ref: Reference | undefined,
): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== EPISODE_OF_CARE_CONTEXT_EXT);
  resource.extension = ref
    ? [...others, { url: EPISODE_OF_CARE_CONTEXT_EXT, valueReference: ref }]
    : others.length ? others : undefined;

  const otherSupporting = (resource.supportingInformation ?? []).filter(
    (r) => !r.reference?.startsWith('EpisodeOfCare/'),
  );
  resource.supportingInformation = ref
    ? [...otherSupporting, ref]
    : otherSupporting.length ? otherSupporting : undefined;
}

/** Set or remove the attendance-source extension. */
export function setAttendanceSource(resource: Appointment, coding: Coding | undefined): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== ATTENDANCE_SOURCE_EXT);
  resource.extension = coding
    ? [...others, { url: ATTENDANCE_SOURCE_EXT, valueCoding: coding }]
    : others.length ? others : undefined;
}

/** Set or remove the reservation-session extension (intake flow). */
export function setReservationSession(resource: Appointment, sessionId: string | undefined): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== RESERVATION_SESSION_EXT);
  resource.extension = sessionId
    ? [...others, { url: RESERVATION_SESSION_EXT, valueString: sessionId }]
    : others.length ? others : undefined;
}

/** Set or remove the reservation-expires-at extension (intake flow). */
export function setReservationExpiresAt(
  resource: Appointment,
  dateTime: string | undefined,
): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== RESERVATION_EXPIRES_AT_EXT);
  resource.extension = dateTime
    ? [...others, { url: RESERVATION_EXPIRES_AT_EXT, valueDateTime: dateTime }]
    : others.length ? others : undefined;
}

/** Remove all reservation-related extensions (called on promotion to booked). */
export function clearReservationExtensions(resource: Appointment): void {
  resource.extension = (resource.extension ?? []).filter(
    (e) => e.url !== RESERVATION_SESSION_EXT && e.url !== RESERVATION_EXPIRES_AT_EXT,
  ) as Extension[] | undefined;
  if (resource.extension?.length === 0) resource.extension = undefined;
}

/** The Chime meeting id (`Appointment.identifier`), if any. */
export function getChimeMeetingId(resource: Appointment): string | undefined {
  return resource.identifier?.find((i) => i.system === CHIME_MEETING_IDENTIFIER_SYSTEM)?.value?.trim() || undefined;
}

function privateValue(resource: Appointment, url: string): Extension | undefined {
  return resource.extension?.find((e) => e.url === url);
}

/** Chime session state (never includes join tokens), or undefined when no field is set. */
export function getChimeSession(resource: Appointment): ChimeSession | undefined {
  const status = privateValue(resource, CHIME_SESSION_EXTS.status)?.valueCode;
  const session: ChimeSession = {
    meetingId: getChimeMeetingId(resource),
    meetingArn: privateValue(resource, CHIME_SESSION_EXTS.meetingArn)?.valueString?.trim() || undefined,
    status: status === 'created' || status === 'active' || status === 'ended' ? status : undefined,
    mediaPipelineId: privateValue(resource, CHIME_SESSION_EXTS.mediaPipelineId)?.valueString?.trim() || undefined,
  };
  return Object.values(session).some((v) => v !== undefined) ? session : undefined;
}

/**
 * Replace the Chime session state: the meeting id identifier and three extensions. Undefined clears all. Every save also
 * drops the retired `recording-status` extension (Decision 39), so older Appointments lose it on their next write.
 */
export function setChimeSession(resource: Appointment, session: ChimeSession | undefined): void {
  const others = (resource.identifier ?? []).filter((i) => i.system !== CHIME_MEETING_IDENTIFIER_SYSTEM);
  const ids: Identifier[] = session?.meetingId
    ? [...others, { system: CHIME_MEETING_IDENTIFIER_SYSTEM, value: session.meetingId }]
    : others;
  resource.identifier = ids.length ? ids : undefined;
  const put = (url: string, ext: Extension | undefined): void =>
    setAppointmentExtensions(resource, url, ext ? [ext] : []);
  put(CHIME_SESSION_EXTS.meetingArn, session?.meetingArn ? { url: CHIME_SESSION_EXTS.meetingArn, valueString: session.meetingArn } : undefined);
  put(CHIME_SESSION_EXTS.status, session?.status ? { url: CHIME_SESSION_EXTS.status, valueCode: session.status } : undefined);
  put(
    CHIME_SESSION_EXTS.mediaPipelineId,
    session?.mediaPipelineId ? { url: CHIME_SESSION_EXTS.mediaPipelineId, valueString: session.mediaPipelineId } : undefined,
  );
  put(CHIME_SESSION_EXTS.recordingStatus, undefined);
}

export function getSessionNotePipelineStatus(
  resource: Appointment,
): SessionNotePipelineStatus | undefined {
  const code = getAppointmentExtensions(resource, SESSION_NOTE_PIPELINE_STATUS_EXT)[0]?.valueCode;
  if (code && (SESSION_NOTE_PIPELINE_STATUS_CODES as readonly string[]).includes(code)) {
    return code as SessionNotePipelineStatus;
  }
  return undefined;
}

export function setSessionNotePipelineStatus(
  resource: Appointment,
  status: SessionNotePipelineStatus | undefined,
): void {
  setAppointmentExtensions(
    resource,
    SESSION_NOTE_PIPELINE_STATUS_EXT,
    status ? [{ url: SESSION_NOTE_PIPELINE_STATUS_EXT, valueCode: status }] : [],
  );
}

export function isSessionTranscriptPipelineQueued(resource: Appointment): boolean {
  return (resource.identifier ?? []).some(
    (i) =>
      i.system === SESSION_TRANSCRIPT_PIPELINE_IDENTIFIER_SYSTEM &&
      i.value === SESSION_TRANSCRIPT_PIPELINE_QUEUED_VALUE,
  );
}

export function setSessionTranscriptPipelineQueued(resource: Appointment, queued: boolean): void {
  const others = (resource.identifier ?? []).filter(
    (i) => i.system !== SESSION_TRANSCRIPT_PIPELINE_IDENTIFIER_SYSTEM,
  );
  const next: Identifier[] = queued
    ? [
        ...others,
        {
          system: SESSION_TRANSCRIPT_PIPELINE_IDENTIFIER_SYSTEM,
          value: SESSION_TRANSCRIPT_PIPELINE_QUEUED_VALUE,
        },
      ]
    : others;
  resource.identifier = next.length ? next : undefined;
}

// ═══════════════════════════════════════════════════════════════════════
//  Link codes (guest link and personal participant links)
// ═══════════════════════════════════════════════════════════════════════

/**
 * A random UUID (v4) from Web Crypto where present (browsers, Node 19+); otherwise Node's `crypto`
 * through the ambient `require` (the Medplum `vmcontext` bot sandbox has no `crypto` global).
 * Never a weak fallback.
 */
function secureRandomUuid(): string {
  const webCrypto = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (typeof webCrypto?.randomUUID === 'function') return webCrypto.randomUUID();
  const req = (globalThis as { require?: (id: string) => { randomUUID: () => string } }).require;
  if (typeof req === 'function') return req('crypto').randomUUID();
  throw new Error('No secure random source is available in this runtime');
}

/** New guest-link code: a random UUID (v4). */
export function generateGuestLinkCode(): string {
  return secureRandomUuid();
}

/** New personal-link code for a client participant: a random UUID (v4). */
export function generateParticipantLinkCode(): string {
  return secureRandomUuid();
}

/** The Appointment's guest-link code (`Appointment.identifier` `guest-link-code`). */
export function getGuestLinkCode(resource: Appointment): string | undefined {
  return resource.identifier?.find((i) => i.system === GUEST_LINK_CODE_IDENTIFIER_SYSTEM)?.value;
}

/** Set or remove the guest-link code identifier, leaving other identifiers in place. */
export function setGuestLinkCode(resource: Appointment, code: string | undefined): void {
  const others = (resource.identifier ?? []).filter((i) => i.system !== GUEST_LINK_CODE_IDENTIFIER_SYSTEM);
  const next: Identifier[] = code ? [...others, { system: GUEST_LINK_CODE_IDENTIFIER_SYSTEM, value: code }] : others;
  resource.identifier = next.length ? next : undefined;
}

// ═══════════════════════════════════════════════════════════════════════
//  Transcript pointer (session-transcript)
// ═══════════════════════════════════════════════════════════════════════

/** The appointment's transcript object in S3 (design Decision 34). Holds no text. */
export interface SessionTranscriptPointer {
  /** The fixed `s3://<bucket>/transcripts/<appointmentId>.txt`; never changes for an appointment. */
  url: string;
  sha256: string;
  size: number;
  /** UTC instant of the last write; availability is derived from it. */
  writtenAt: string;
}

/** S3 lifecycle rules expire the transcript 7 days after its last write. */
export const SESSION_TRANSCRIPT_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;

/** The S3 key of an appointment's transcript. */
export function sessionTranscriptKey(appointmentId: string): string {
  return `transcripts/${appointmentId}.txt`;
}

/** The fixed transcript URL for an appointment in `bucket`. */
export function sessionTranscriptUrl(bucket: string, appointmentId: string): string {
  return `s3://${bucket}/${sessionTranscriptKey(appointmentId)}`;
}

/** True for `s3://<bucket>/transcripts/<appointmentId>.txt` naming this appointment, so a forged pointer can't address another object. */
export function isSessionTranscriptUrlFor(url: string, appointmentId: string): boolean {
  const match = /^s3:\/\/([a-z0-9][a-z0-9.-]{1,61}[a-z0-9])\/(.+)$/.exec(url);
  return Boolean(match) && match![2] === sessionTranscriptKey(appointmentId);
}

/** The transcript pointer, or undefined when absent or malformed. */
export function getSessionTranscriptPointer(resource: Appointment): SessionTranscriptPointer | undefined {
  const parent = getAppointmentExtensions(resource, SESSION_TRANSCRIPT_EXT)[0];
  const sub = (url: string) => parent?.extension?.find((e) => e.url === url);
  const url = sub(SESSION_TRANSCRIPT_SUB.url)?.valueUrl;
  const sha256 = sub(SESSION_TRANSCRIPT_SUB.sha256)?.valueString;
  const size = sub(SESSION_TRANSCRIPT_SUB.size)?.valueInteger;
  const writtenAt = sub(SESSION_TRANSCRIPT_SUB.writtenAt)?.valueInstant;
  if (!url || !sha256 || size == null || !writtenAt || Number.isNaN(Date.parse(writtenAt))) return undefined;
  if (resource.id && !isSessionTranscriptUrlFor(url, resource.id)) return undefined;
  return { url, sha256, size, writtenAt };
}

/** Replace the transcript pointer . Undefined removes it. */
export function setSessionTranscriptPointer(resource: Appointment, pointer: SessionTranscriptPointer | undefined): void {
  setAppointmentExtensions(
    resource,
    SESSION_TRANSCRIPT_EXT,
    pointer
      ? [
          {
            url: SESSION_TRANSCRIPT_EXT,
            extension: [
              { url: SESSION_TRANSCRIPT_SUB.url, valueUrl: pointer.url },
              { url: SESSION_TRANSCRIPT_SUB.sha256, valueString: pointer.sha256 },
              { url: SESSION_TRANSCRIPT_SUB.size, valueInteger: pointer.size },
              { url: SESSION_TRANSCRIPT_SUB.writtenAt, valueInstant: pointer.writtenAt },
            ],
          },
        ]
      : [],
  );
}

/** True until 7 days after the transcript's last write (a reader that gets a 404 still treats it as gone). */
export function isSessionTranscriptAvailable(pointer: SessionTranscriptPointer, nowMs = Date.now()): boolean {
  const writtenMs = Date.parse(pointer.writtenAt);
  return !Number.isNaN(writtenMs) && nowMs - writtenMs < SESSION_TRANSCRIPT_RETENTION_MS;
}

/** The transcription Lambda's result, as `chime-events` receives it from EventBridge (design Decision 34). */
export interface SessionTranscriptResultDetail {
  appointmentId: string;
  meetingId: string;
  mediaPipelineId: string;
  outcome: 'transcript-ready' | 'no-audio' | 'failed';
  /** Present only for `transcript-ready`. */
  transcript?: SessionTranscriptPointer;
  /** An interim pointer, published after each written segment: only the pointer changes, never the status. */
  partial?: boolean;
  /** The run's final result, and it left no folder of the meeting to another run, whichever capture triggered it. */
  meetingDone?: boolean;
}

/** Appointment-side transcript statuses, shared by the appointment badge and `check-transcript-status`. */
export type ChimeTranscriptStatus = 'processing' | 'ready' | 'drafting' | 'failed' | 'not-recorded' | 'deleted';

/** Processing this long after the appointment's end (or start) shows as Failed, or Ready if a transcript exists. */
export const CHIME_TRANSCRIPT_PROCESSING_WINDOW_MS = 60 * 60 * 1000;

/**
 * Derives the transcript status from the status code and the transcript pointer; nothing is stored (design Decision
 * 34). `undefined` means nothing has been recorded or stamped (Not recorded once the session has ended). `noConsent`
 * marks Not recorded because the provider-join consent check refused (`opted_out`). A transcript wins over a failure,
 * so an appointment with a pointer is never Failed.
 */
export function chimeTranscriptStatus(
  resource: Appointment,
  nowMs = Date.now(),
): { status: ChimeTranscriptStatus; noConsent?: true } | undefined {
  const code = getSessionNotePipelineStatus(resource);
  const pointer = getSessionTranscriptPointer(resource);
  const stored = pointer ? { status: isSessionTranscriptAvailable(pointer, nowMs) ? ('ready' as const) : ('deleted' as const) } : undefined;
  if (code === 'generating') return { status: 'drafting' };
  if (code === 'queued' || code === 'transcribing') {
    const refMs = Date.parse(resource.end ?? resource.start ?? '');
    const late = !Number.isNaN(refMs) && nowMs - refMs > CHIME_TRANSCRIPT_PROCESSING_WINDOW_MS;
    if (!late) return { status: 'processing' };
    return stored ?? { status: 'failed' };
  }
  if (stored) return stored;
  if (code === 'failed') return { status: 'failed' };
  if (code === 'opted_out') return { status: 'not-recorded', noConsent: true };
  return undefined;
}

/**
 * Result of bot `sign-appointment-note`. The signature is committed whenever a result is returned;
 * `sealed` false means the follow-up `appointment-tools` `seal-appointment` failed and can be
 * retried on its own (send `noteVersionId`) without re-signing.
 */
export interface SignAppointmentNoteResult {
  signed: true;
  sealed: boolean;
  noteId: string;
  /** The signed `meta.versionId` the seal is bound to, when the transaction reported it. */
  noteVersionId?: string;
  /** The sealed Appointment when `sealed` is true. */
  appointment?: Appointment;
  /** The seal error text when `sealed` is false. */
  sealError?: string;
}

/** True for a `sign-appointment-note` result object (as opposed to a bare Appointment). */
export function isSignAppointmentNoteResult(value: unknown): value is SignAppointmentNoteResult {
  if (value === null || typeof value !== 'object') return false;
  const candidate = value as Partial<SignAppointmentNoteResult>;
  return candidate.signed === true && typeof candidate.sealed === 'boolean' && typeof candidate.noteId === 'string';
}
